/*
 * FractalAI research-route registry / FractalAI 研究路由注册表
 *
 * 中文：
 * - 本文件只保存静态路由展示元数据；匹配、URL 参数解析和文档标题生成仍由
 *   researchNavigation.js 负责。
 * - 将大体量声明式数据与导航算法分离，便于逐业务域审阅，也避免继续扩大
 *   应用壳层工具文件。
 * - autoResearchLane 由调用方显式注入，防止本模块反向依赖导航实现。
 *
 * English:
 * - This file contains declarative route presentation metadata only. Matching,
 *   URL-context parsing, and document-title generation remain in
 *   researchNavigation.js.
 * - Separating the large registry from navigation logic keeps the application
 *   shell utility reviewable and prevents a monolithic source module.
 * - autoResearchLane is injected explicitly so this registry does not depend
 *   back on the navigation implementation.
 *
 * Production boundary / 生产边界:
 * - No API calls, authorization decisions, database access, or browser-side
 *   fact derivation may be added here.
 */

export const buildResearchRouteMeta = autoResearchLane => [
  {
    path: '/',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: '品牌首屏',
    title: 'FractalAI',
    description: '以极简而集中的方式呈现 FractalAI 的市场观、边界感与整体气质。',
    modeLabel: '品牌首屏',
    tone: 'accent',
    recommendedPath: '/home',
    recommendedLabel: '进入首页索引',
  },
  {
    path: '/home',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: '品牌索引',
    title: 'FractalAI 首页索引',
    description: '索引价值观、方法论、AI 与技术矩阵，并连接各研究方向。',
    modeLabel: '品牌索引页',
    tone: 'accent',
    recommendedPath: '/foundations/methodology',
    recommendedLabel: '继续查看方法论',
  },
  {
    path: '/dashboard/workplace',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '研究总控',
    stageLabel: '驾驶舱',
    title: '智能驾驶舱',
    description: '统筹市场、个股、观察池与图表验证入口，让研究入口和当前进度保持同一视角。',
    modeLabel: '总控入口',
    tone: 'accent',
  },
  {
    path: '/foundations/values',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: '价值观',
    title: '价值观',
    description: '面向高净值客户与研究型客户，说明 FractalAI 如何看待市场、风险与人机协同。',
    modeLabel: '品牌说明页',
    tone: 'strong',
    recommendedPath: '/foundations/methodology',
    recommendedLabel: '继续查看方法论',
  },
  {
    path: '/foundations/methodology',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: '方法论',
    title: '方法论',
    description: '解释分型结构为何是系统核心语言，以及论文、系统与智能层如何被统一到同一研究框架中。',
    modeLabel: '品牌说明页',
    tone: 'accent',
    recommendedPath: '/foundations/ai',
    recommendedLabel: '继续查看 AI 与系统演进',
  },
  {
    path: '/foundations/ai',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: 'AI 与系统演进',
    title: 'AI 与系统演进',
    description: '解释人工智能在 FractalAI 中如何同时服务工程实现与研究增强，并明确其边界。',
    modeLabel: '品牌说明页',
    tone: 'accent',
    recommendedPath: autoResearchLane,
    recommendedLabel: '进入自动研究所',
  },
  {
    path: '/foundations/technology',
    exact: true,
    activeLane: '/dashboard/workplace',
    sectionLabel: '品牌与方法层',
    stageLabel: '技术矩阵',
    title: '技术矩阵',
    description: '说明 FractalAI 如何建立在开放世界技术之上，并把这些能力整合成统一研究系统。',
    modeLabel: '品牌说明页',
    tone: 'strong',
    recommendedPath: '/dashboard/workplace',
    recommendedLabel: '进入研究系统',
  },
  {
    path: '/market/overview',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '正式架构',
    stageLabel: '市场工作台',
    title: '全市场行情',
    description: '宏观架构下的正式市场工作区入口，承接市场总览、结构扫描和后续语义化子空间。',
    modeLabel: '正式路由',
    tone: 'accent',
  },
  {
    path: '/market/sectors',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '正式架构',
    stageLabel: '板块轮动',
    title: '板块轮动',
    description: '全市场行情下的正式子空间，负责把热点、轮动轴和研究回流组织到同一层。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/market/breadth',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '正式架构',
    stageLabel: '市场广度',
    title: '市场广度',
    description: '全市场行情下的正式子空间，承接跟踪对象、观察池覆盖和研究输出广度。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/market/alerts',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '正式架构',
    stageLabel: '市场信号中心',
    title: '市场信号中心',
    description: '全市场行情下的正式子空间，承接结构变化、热点异动和研究接力入口。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/validation/market',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '兼容工作区',
    stageLabel: '市场兼容跳转',
    title: '全市场行情',
    description: 'validation 市场页已经收束为兼容跳转层，正式市场入口现在统一进入全市场行情工作台。',
    modeLabel: '兼容跳转',
    tone: 'accent',
    recommendedPath: '/market/overview',
    recommendedLabel: '打开正式市场工作台',
  },
  {
    path: '/research/symbol',
    exact: true,
    activeLane: '/research/symbol',
    sectionLabel: '正式架构',
    stageLabel: '个股研究',
    title: '个股研究',
    description: '宏观架构下的正式个股研究入口，承接单标的结构分析、研究接力和图表对照。',
    modeLabel: '正式路由',
    tone: 'accent',
  },
  {
    path: '/research/symbol/',
    exact: false,
    activeLane: '/research/symbol',
    sectionLabel: '正式架构',
    stageLabel: '个股研究详情',
    title: '个股研究',
    description: 'research 体系下的对象型详情路由，承接单标的结构分析、研究接力和图表对照。',
    modeLabel: '正式详情页',
    tone: 'accent',
  },
  {
    path: '/research/watchlists',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '自选股 · 持续跟踪',
    title: '自选股 · 持续跟踪',
    description: '聚合人工提名、首次覆盖进度、待确认、有效 TrackingRecord 和历史，并以多股票同屏看板呈现持续跟踪变化。',
    modeLabel: '业务研究工作台',
    tone: 'accent',
  },
  {
    path: '/research/index/',
    exact: false,
    activeLane: '/research/symbol',
    sectionLabel: '正式架构',
    stageLabel: '指数研究',
    title: '指数研究',
    description: 'research 体系下的正式对象页，承接指数级结构阅读与市场语境。',
    modeLabel: '正式详情页',
    tone: 'strong',
  },
  {
    path: '/research/compare',
    exact: true,
    activeLane: '/research/symbol',
    sectionLabel: '正式架构',
    stageLabel: '标的对比研究',
    title: '标的对比研究',
    description: 'research 体系下的正式子空间，承接双对象对照和相对强弱观察。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/validation/symbol',
    exact: true,
    activeLane: '/research/symbol',
    sectionLabel: '兼容工作区',
    stageLabel: '个股兼容跳转',
    title: '个股分析',
    description: 'validation 个股页已经收束为兼容跳转层，正式个股研究现在统一进入 research 研究树。',
    modeLabel: '兼容跳转',
    tone: 'accent',
    recommendedPath: '/research/symbol',
    recommendedLabel: '打开正式个股研究',
  },
  {
    path: '/company/analysis',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '公告新闻稿',
    title: '上市公司公告新闻稿',
    description: '面向交易所公告的新闻稿生成与人工复核入口，负责公告浏览、证据检索、历史草稿、复核和导出。',
    modeLabel: '公告与新闻稿',
    tone: 'accent',
  },
  {
    path: '/intelligence/strategy8-coverage',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '跟踪覆盖',
    title: '首次覆盖与持续追踪',
    description: '5分钟首次预警触发首次覆盖，由业务决定是否进入持续追踪名单，并按周生成研究报告。',
    modeLabel: '业务研究工作台',
    tone: 'accent',
  },
  {
    path: '/intelligence/industry-mapping-review',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '行业映射复核',
    title: '发行人行业映射复核',
    description: '复核发行人披露的经济活动到 GB/T 4754-2017 行业大类的唯一映射，并保留来源指纹与追加式人工历史。',
    modeLabel: '数据治理工作台',
    tone: 'accent',
  },
  {
    path: '/intelligence/review/announcement-events',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '标签治理',
    title: '公告事件标签治理',
    description: '围绕交易所官方公告完成两轮独立业务标注、冲突裁决和负责人批准。',
    modeLabel: '内部模型治理',
    tone: 'accent',
  },
  {
    path: '/strategy/library',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '策略与报告中心',
    title: '策略与报告中心',
    description: '宏观架构下的研究中台入口，负责把策略结果、报告资产和观察池回流组织成一体。',
    modeLabel: '正式路由',
    tone: 'accent',
  },
  {
    path: '/strategy/import',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '策略导入',
    title: '策略导入',
    description: '正式语义路由下的导入入口，继续复用现有执行链承接策略回流。',
    modeLabel: '正式路由',
    tone: 'strong',
  },
  {
    path: '/strategy/signals',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '策略信号中心',
    title: '策略信号中心',
    description: '策略体系下的正式子空间，承接候选信号、多周期观察和下游研究接力。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/strategy/signals/breakoutwatch',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '震霄预警池',
    title: '震霄：双阻力突破',
    description: '震霄正式工作台，直接展示预警1/预警2当前池，并承接到个股研究。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/strategy/signals/uptrend-double-resistance',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '扶摇信号池',
    title: '扶摇：上升双阻突破',
    description: '扶摇正式工作台，承接上升走势双阻力突破确认信号，并准备接入个股研究下钻。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/strategy/signals/valid-wave-pivot',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '灵枢首次预警池',
    title: '灵枢：有效波中枢',
    description: '灵枢正式工作台，承接有效波趋势首次预警列表，并准备展示1分钟和5分钟近五个交易日结果。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/strategy/',
    exact: false,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '策略详情',
    title: '策略详情',
    description: 'strategy 体系下的正式对象页，承接单策略阅读、周期语义和研究回跳。',
    modeLabel: '正式详情页',
    tone: 'strong',
  },
  {
    path: '/reports/list',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '报告中心',
    title: '报告中心',
    description: '正式语义路由下的研究输出入口，承接报告分类、归档和研究回看。',
    modeLabel: '正式路由',
    tone: 'strong',
  },
  {
    path: '/reports/archive',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '报告归档',
    title: '报告归档',
    description: '报告体系下的正式子空间，承接研究输出的长期沉淀、分类回看和研究回跳。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/reports/hotspot',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '热点报告',
    title: '热点报告',
    description: 'reports 体系下的热点阅读页，直接在浏览器中展示盘后热点分析报告。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/reports/attribution',
    exact: true,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '归因报告',
    title: '归因报告',
    description: 'reports 体系下的归因阅读页，直接在浏览器中展示策略归因报告。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/reports/category/',
    exact: false,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '分类报告视图',
    title: '分类报告视图',
    description: 'reports 体系下的中层详情页，承接某一研究分类下的连续阅读。',
    modeLabel: '正式详情页',
    tone: 'strong',
  },
  {
    path: '/reports/',
    exact: false,
    activeLane: '/strategy/library',
    sectionLabel: '正式架构',
    stageLabel: '报告详情',
    title: '报告详情',
    description: 'reports 体系下的对象详情页，承接单报告阅读、引用和研究回跳。',
    modeLabel: '正式详情页',
    tone: 'strong',
  },
  {
    path: '/intelligence/terminal',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '研究所总屏',
    title: '自动研究所',
    description: '以 CoverageCase 生命周期为主轴，汇总策略8、策略9与受审计人工提名准入的首次覆盖、持续跟踪、证据和运行状态。',
    modeLabel: '总入口',
    tone: 'accent',
  },
  {
    path: '/intelligence/finance-mcp',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: 'FinanceMCP',
    title: '公司事实与证据工作台',
    description: '按公司代码和研究截止日只读核对公司概览、财务、公告、本地行业、同行趋势和来源证据。',
    modeLabel: '专业工作台',
    tone: 'strong',
  },
  {
    path: '/intelligence/quant-mcp',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: 'QuantMCP',
    title: '量化研究工作台',
    description: '执行确定性、无状态、只读的风险、估值、固定收益、期权和研究产物查询。',
    modeLabel: '专业工作台',
    tone: 'strong',
  },
  {
    path: '/intelligence/global-intel',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: 'GlobalIntelMCP',
    title: '全球情报工作台',
    description: '按截止日查看全球情报来源契约、事件、实体关系和卫星产品元数据。',
    modeLabel: '专业工作台',
    tone: 'strong',
  },
  {
    path: '/intelligence/workbench',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '交易复盘',
    title: '每日复盘',
    description: '按交易动作组织系统证据、人工备注、策略信号和报告草稿，并保留讨论、分型问答与正式报告模式。',
    modeLabel: '研究工作台',
    tone: 'accent',
  },
  {
    path: '/intelligence/fractal',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '分型智能',
    title: '分型智能',
    description: '自动研究所下的正式子空间，承接结构解释、多周期摘要和分型研究材料。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/intelligence/macro',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '国内行业景气度',
    title: '国内行业景气度',
    description: '分层组织国内宏观底座、实体行业景气事实目标、策略6/7行业分布领先代理及市场确认。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/intelligence/fusion',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '共振智能',
    title: '共振智能',
    description: '自动研究所下的正式子空间，负责形成结构、宏观和策略结果的综合判断层。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/intelligence/explainer',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '自动研究所',
    stageLabel: '解释中心',
    title: '解释中心',
    description: '自动研究所下的正式子空间，承接来源、证据、置信度和研究回跳容器。',
    modeLabel: '正式子空间',
    tone: 'strong',
  },
  {
    path: '/validation/lightweight-lab',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '图表验证',
    stageLabel: '高性能图表',
    title: '高性能图表工作台',
    description: '高性能图表工作台用于验证 Lightweight 图表接管条件，并保留与现有图表的对照入口。',
    modeLabel: '验证页面',
    tone: 'accent',
  },
  {
    path: '/dashboard/marketKLine',
    exact: true,
    activeLane: '/market/overview',
    sectionLabel: '兼容验证入口',
    stageLabel: '旧市场验证',
    title: '旧市场验证入口',
    description: '这是保留中的旧市场验证页，建议优先从新的市场工作区进入，同步保留旧图表行为。',
    modeLabel: '旧入口',
    tone: 'warning',
    recommendedPath: '/market/overview',
    recommendedLabel: '切回市场工作台',
  },
  {
    path: '/dashboard/symbolKLine',
    exact: true,
    activeLane: '/research/symbol',
    sectionLabel: '兼容验证入口',
    stageLabel: '旧个股验证',
    title: '旧个股验证入口',
    description: '这是保留中的旧个股验证页，适合做历史对照，但新的个股工作台已经接管导航和语义层。',
    modeLabel: '旧入口',
    tone: 'warning',
    recommendedPath: '/research/symbol',
    recommendedLabel: '切回个股研究',
  },
  {
    path: '/lightweight-chart-workbench',
    exact: true,
    activeLane: autoResearchLane,
    sectionLabel: '图表工作台',
    stageLabel: '轻量图表',
    title: 'Lightweight 图表工作台',
    description: '轻量图表作为正式研究图表入口，与现有 TChart 并行运行。',
    modeLabel: '图表工作台',
    tone: 'accent',
    recommendedPath: autoResearchLane,
    recommendedLabel: '切回自动研究所',
  },
];
